import { Component } from '@theme/component';

class HeaderShopMenu extends Component {
  /** Triggers live in the nav; their panels are rendered outside the component. */
  get triggers() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('button[aria-controls][aria-expanded]')]);
  }

  get openTrigger() {
    return this.triggers.find((trigger) => trigger.getAttribute('aria-expanded') === 'true') ?? null;
  }

  /** @param {Element | null} trigger */
  panelFor(trigger) {
    const id = trigger?.getAttribute('aria-controls');

    return id ? /** @type {HTMLElement | null} */ (document.getElementById(id)) : null;
  }

  get panel() {
    return this.panelFor(this.openTrigger ?? this.refs.shopTrigger);
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  get isOpen() {
    return this.openTrigger != null;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('keydown', this.handleDocumentKeydown);
    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleDocumentClick);
    document.removeEventListener('keydown', this.handleDocumentKeydown);
    window.removeEventListener('resize', this.handleResize);
  }

  toggle = (event) => {
    event.preventDefault();

    const target = event.target;
    const trigger = target instanceof Element ? target.closest('button[aria-controls]') : null;

    if (!(trigger instanceof HTMLElement)) return;

    if (trigger.getAttribute('aria-expanded') === 'true') {
      this.close();
      return;
    }

    this.open(trigger);
  };

  open = (trigger = this.refs.shopTrigger) => {
    const panel = this.panelFor(trigger instanceof HTMLElement ? trigger : null);

    if (!(panel instanceof HTMLElement) || !(trigger instanceof HTMLElement)) return;

    // Only one panel open at a time.
    if (this.openTrigger && this.openTrigger !== trigger) this.close();

    this.ensureActiveMenu(panel);
    panel.hidden = false;
    panel.dataset.state = 'open';
    trigger.setAttribute('aria-expanded', 'true');
    this.updatePanelHeight();
    this.headerComponent?.dispatchEvent(new CustomEvent('shop-menu-state-change'));
  };

  close = (returnFocus = false) => {
    const trigger = this.openTrigger;
    const panel = this.panelFor(trigger);

    if (!(panel instanceof HTMLElement) || !(trigger instanceof HTMLElement)) return;

    panel.hidden = true;
    delete panel.dataset.state;
    trigger.setAttribute('aria-expanded', 'false');
    this.resetPanelHeight();

    if (returnFocus) {
      trigger.focus();
    }
    this.headerComponent?.dispatchEvent(new CustomEvent('shop-menu-state-change'));
  };

  activateMenu = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tab = target.closest('[data-menu-tab]');
    if (!(tab instanceof HTMLElement)) return;

    this.open(this.refs.shopTrigger);
    this.setActiveMenu(tab.dataset.menuTab || '1');
  };

  handleTabKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !(this.panel instanceof HTMLElement)) return;

    const tabs = [...this.panel.querySelectorAll('[data-menu-tab]')].filter((tab) => tab instanceof HTMLElement);
    const currentIndex = tabs.indexOf(target);

    if (currentIndex === -1) return;

    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();

    const nextTab = /** @type {HTMLElement | undefined} */ (tabs[nextIndex]);
    if (!(nextTab instanceof HTMLElement)) return;

    nextTab.focus();
    this.setActiveMenu(nextTab.dataset.menuTab || '1');
  };

  handleDocumentClick = (event) => {
    if (!this.isOpen || !(event.target instanceof Node)) return;

    if (this.contains(event.target) || this.panel?.contains(event.target)) return;

    this.close();
  };

  handleDocumentKeydown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;

    this.close(true);
  };

  handleResize = () => {
    if (!this.isOpen) return;

    this.updatePanelHeight();
  };

  /** @param {HTMLElement} panel — panels without tabs (e.g. explore) are a no-op. */
  ensureActiveMenu(panel = this.panel) {
    if (!(panel instanceof HTMLElement)) return;

    const activeTab = panel.querySelector('[data-menu-tab][aria-selected="true"]');
    const firstTab = panel.querySelector('[data-menu-tab]');
    const tab = activeTab || firstTab;

    if (!(tab instanceof HTMLElement)) return;

    this.setActiveMenu(tab.dataset.menuTab || '1');
  }

  setActiveMenu(menuId) {
    if (!(this.panel instanceof HTMLElement)) return;

    const tabs = this.panel.querySelectorAll('[data-menu-tab]');
    const panels = this.panel.querySelectorAll('[data-menu-panel]');

    tabs.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      const isActive = item.dataset.menuTab === menuId;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-selected', isActive ? 'true' : 'false');
      item.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    panels.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      const isActive = item.dataset.menuPanel === menuId;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this.updatePanelHeight();
  }

  updatePanelHeight() {
    if (!(this.panel instanceof HTMLElement) || !(this.headerComponent instanceof HTMLElement)) return;

    const activePanel = this.panel.querySelector('[data-menu-panel].is-active');
    const panelHeight = activePanel instanceof HTMLElement ? activePanel.offsetHeight : this.panel.offsetHeight;
    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
    const topRow = /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'));
    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0 ? topRow?.offsetHeight ?? 0 : this.headerComponent.offsetHeight;

    this.headerComponent.style.setProperty('--submenu-height', `${panelHeight}px`);
    this.headerComponent.style.setProperty('--full-open-header-height', `${panelHeight + headerVisibleHeight}px`);
  }

  resetPanelHeight() {
    if (!(this.headerComponent instanceof HTMLElement)) return;

    this.headerComponent.style.setProperty('--submenu-height', '0px');
    this.headerComponent.style.setProperty('--full-open-header-height', '0px');
  }
}

if (!customElements.get('header-shop-menu')) {
  customElements.define('header-shop-menu', HeaderShopMenu);
}